import { useMemo } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { isUserPostTreatment } from "@/hooks/usePatientStatus";
import useTransitionTrackStatus from "@/hooks/useTransitionTrackStatus";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";

interface AppUserTransitionTrackStatus {
  hasSupportTransitionTrackPatients: boolean;
  isAppUserPatientInTransitionTrack: boolean;
  supportTransitionTrackPatients: LinkedPatientProfile[];
  wasAppUserEverPatientInTransitionTrack: boolean;
}

const useAppUserTransitionTrackStatus = (): AppUserTransitionTrackStatus => {
  const { externalId: appUserId, isPatient: isAppUserPatient } =
    useAppUserSelector();

  const proxyPatients = useAppUserProxyPatientsSelector();

  const { isUserInTransitionTrack, wasUserEverInTransitionTrack } =
    useTransitionTrackStatus();

  const isAppUserPatientInTransitionTrack = useMemo(
    () => isAppUserPatient && isUserInTransitionTrack(appUserId),
    [appUserId, isAppUserPatient, isUserInTransitionTrack],
  );

  const wasAppUserEverPatientInTransitionTrack = useMemo(
    () => isAppUserPatient && wasUserEverInTransitionTrack(appUserId),
    [appUserId, isAppUserPatient, wasUserEverInTransitionTrack],
  );

  const supportTransitionTrackPatients = useMemo(
    () =>
      (proxyPatients ?? []).filter(
        (p) =>
          !isNil(p.universalStatusCode) &&
          // post-treatment patients are omitted/ignored
          !isUserPostTreatment(p.universalStatusCode) &&
          wasUserEverInTransitionTrack(p.externalId),
      ),
    [proxyPatients, wasUserEverInTransitionTrack],
  );

  const hasSupportTransitionTrackPatients = useMemo(
    () => !isEmpty(supportTransitionTrackPatients),
    [supportTransitionTrackPatients],
  );

  return {
    hasSupportTransitionTrackPatients,
    isAppUserPatientInTransitionTrack,
    supportTransitionTrackPatients,
    wasAppUserEverPatientInTransitionTrack,
  };
};

export type { AppUserTransitionTrackStatus };
export default useAppUserTransitionTrackStatus;
